import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Outlet, useLocationPathname, useNavigate } from '@rocketlane/rocket-router';
import { useEffect } from 'react';
import styled from 'styled-components';
import { NitroClawDesktopSidebar } from 'components/organisms/nitro-claw/nitro-claw-desktop-sidebar';
import { useAppShellMode } from 'hooks/use-app-shell-mode';
import { useQuickEntryBridge } from 'hooks/use-quick-entry-bridge';
import { useSyncQuickEntryNative } from 'hooks/use-quick-entry-config';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { isTauriApp } from 'utils/is-tauri';
import { ROUTE_PATHS } from './routes-paths';
/** Fills the viewport; gutter behind the inset shell uses page background. */ const DesktopShellViewport = styled.div`
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  background: var(--page-background);
`;
/**
 * Sidebar + outlet as one floating card — 8px inset and radius match the
 * classic Rocketlane LeftNav island chrome.
 */ const DesktopShellRoot = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  box-sizing: border-box;
  width: calc(100% - 16px);
  height: calc(100vh - 16px);
  max-height: calc(100vh - 16px);
  margin: 8px;
  overflow: hidden;
  border-radius: var(--border-radius-small);
  background: var(--page-background);
`;
const DesktopShellOutlet = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /*
   * Page (molecules/page) is inset + rounded for web LeftNav chrome.
   * In the desktop shell the outer card already provides the island radius,
   * so flush #page to the outlet top/edges (Projects, Notifications, etc.).
   * Keep a column flex fill so island pages (sheets) size to the outlet
   * instead of 100vh and misalign right rails.
   */
  #page {
    height: 100% !important;
    max-height: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    min-height: 0 !important;
    overflow: hidden !important;

    & > *:first-child {
      border-radius: 0 !important;
    }

    & > *:last-child {
      border-radius: 0 !important;
    }

    #page_header {
      border-radius: 0 !important;
    }
  }
`;
/**
 * Full-viewport Nitro Claw chrome — sidebar + outlet.
 * Used in Tauri always, and on web when `app-shell-mode` is `nitro-claw`.
 */ export const DesktopAppShell = ()=>{
    const pathname = useLocationPathname();
    const navigate = useNavigate();
    const { mode } = useAppShellMode();
    useDismissBootSkeleton(true);
    useQuickEntryBridge();
    // Push shortcut / background prefs to Rust while the main shell is mounted.
    useSyncQuickEntryNative();
    useEffect(()=>{
        if (isTauriApp() || mode !== 'nitro-claw') {
            return;
        }
        const atHome = pathname === ROUTE_PATHS.ROOT || pathname === ROUTE_PATHS.EMPTY || pathname === '/';
        if (atHome) {
            navigate(`/${ROUTE_PATHS.NITRO_CLAW}`, {
                replace: true
            });
        }
    }, [
        mode,
        navigate,
        pathname
    ]);
    return /*#__PURE__*/ _jsx(DesktopShellViewport, {
        children: /*#__PURE__*/ _jsxs(DesktopShellRoot, {
            "data-cy": "desktop-app-shell",
            "data-app-shell": "nitro-claw",
            children: [
                /*#__PURE__*/ _jsx(NitroClawDesktopSidebar, {}),
                /*#__PURE__*/ _jsx(DesktopShellOutlet, {
                    children: /*#__PURE__*/ _jsx(Outlet, {})
                })
            ]
        })
    });
};
